import React from 'react';

import { ReactComponent as AddIcon } from '@assets/svg/add.svg';
import { ReactComponent as ThreeDotsIcon } from '@assets/svg/three-dots.svg';

import TableEmptyRow from '@components/common/TableEmptyRow/TableEmptyRow';
import Button from '@components/uikit/Button/Button';

import { DEFAULT_CURRENCY } from '@constants/currency';
import { MODAL_NAMES } from '@constants/modalRenderers';

import useTranslation from '@hooks/useTranslation';

import { useTemplateCurrenciesListQuery } from '@pages/MultiplicationFactors/services/useTemplateCurrenciesListQuery';

import { useTemplatesListQuery } from '@services/useTemplatesListQuery';

import { useModalActions } from '@store/useModalStore';

import { formatToCurrencyCeil } from '../../helpers/formatToCurrencyCeil';
import { useEquipmentSettingsListQuery } from '../../services/useEquipmentSettingsListQuery';
import TableCostSavingTemplatesSkeleton from './TableCostSavingTemplatesSkeleton';
import ContextMenu from './components/ContextMenu';
import style from './style.module.scss';

const CostSavingTemplates = () => {
  const { t } = useTranslation();
  const { setActiveModal } = useModalActions();

  const { data: templatesListData, isLoading: templatesListIsLoading } = useTemplatesListQuery();
  const { data: equipmentSettingsListData } = useEquipmentSettingsListQuery();
  const { data: templateCurrenciesListData } = useTemplateCurrenciesListQuery();

  const customerSettings = equipmentSettingsListData?.customerSettings;

  const currencies = templateCurrenciesListData || [];

  return (
    <div className={style.costSavingTemplatesWrapper}>
      <div className={style.costSavingTemplatesHeader}>
        <h4 className={style.costSavingTemplatesTitle}>{t('Cost Saving Templates')}</h4>
        <Button
          text={t('Add new template')}
          type="secondary"
          leftElement={<AddIcon className={style.addIcon} />}
          onClick={() =>
            setActiveModal({
              name: MODAL_NAMES.costSavingTemplateModal,
              options: { customerSettings, currencies },
            })
          }
        />
      </div>
      <table className={style.costSavingTemplatesTable}>
        <thead>
          <tr>
            <th className={style.templateNameColumn}>{t('Template Name')}</th>
            <th className={style.equipmentTotalColumn}>{t('Equipment Total')}</th>
            <th className={style.multiplicationFactorColumn}>{t('Multiplication Factor')}</th>
            <th className={style.actionsColumn} />
          </tr>
        </thead>
        <tbody>
          {templatesListIsLoading ? (
            <TableCostSavingTemplatesSkeleton />
          ) : templatesListData?.length ? (
            templatesListData.map((row) => (
              <tr key={row.id} className={style.costSavingTemplatesBodyRow}>
                <td className={style.templateNameColumn}>{row.name}</td>
                <td className={style.equipmentTotalColumn}>{row.settings?.length || 0}</td>
                <td className={style.multiplicationFactorColumn}>
                  {row.currency?.symbol || DEFAULT_CURRENCY.symbol}{' '}
                  {formatToCurrencyCeil(row.multiplication_factor.toString())}
                </td>
                <td className={style.actionsColumn}>
                  <div
                    className={style.actionsIconContainer}
                    data-tooltip-id="table-cost-saving-templates-action-menu"
                    data-tooltip-content={JSON.stringify(row)}
                  >
                    <ThreeDotsIcon />
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <TableEmptyRow text={t('No templates')} />
          )}
        </tbody>
      </table>
      {customerSettings && (
        <ContextMenu id="table-cost-saving-templates-action-menu" currencies={currencies} />
      )}
    </div>
  );
};

export default CostSavingTemplates;
